import React from "react";
import { useNavigate } from "react-router-dom";
import {
  DeleteFilled,
  InfoCircleOutlined,
  ExportOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { t } from "i18next";
import moment from "moment";
import {
  Row,
  Col,
  Table,
  Breadcrumb,
  Button,
  Drawer,
  Form,
  Tabs,
  DatePicker,
  SearchSelectInput,
} from "common/elements";
import "./index.css";
import { Util } from "common/util";
import { getHeaders, getURl } from "services/core";

const { RangePicker } = DatePicker;

export default function withDataTable({
  title = "",
  addNewLink = "",
  addImportLink = "",
  addViewLink = "",
  pathName = "/",
  dateFormat = "",
  datePlaceholder = "",
  pickerDate = "",
  columns = [],
  defaultPageSize = 20,
  topFilterItems = [],
  getData = () => {},
  showFilter = false,
  showDate = false,
  showButtonImport = false,
  showButtonInfo = false,
  showButtonDelete = true,
  showFetchListDate = true,
  tableScrollY = null,
  FormReview = () => {},
  FormInsertOrUpdate = () => {},
  handleShowDetail = (id, reviewRef) => {},
  confirmSoftDelete = (id) => {},
  componentWillMount = () => {},
  handleSearch = (e) => {},
  filterFields = () => {},
  buttonOption = () => { },
}) {
  return () => {
    const [datas, setDatas] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [form] = Form.useForm();
    const params = new URLSearchParams(document.location.search);
    const navigate = useNavigate();
    let reviewRef = React.createRef();

    const fetchList = () => {
      setLoading(true);
      getData()
        .then((response) => {
          setDatas(response);
        })
        .catch((error) => {
          if (error.response.status === 403) {
            localStorage.removeItem("ACCESS_TOKEN");
            navigate("/signin");
          }
        })
        .finally(() => setLoading(false));
    };

    const onShowDetail = (id) => {
      if (addViewLink) {
        navigate(`${addViewLink}/${id}`);
      } else {
        handleShowDetail(id, reviewRef);
      }
    };

    const onDelete = (id) => {
      Util.getInstance()
        .sweetAlertConfirm(
          t("text_confrim"),
          "You won't be able to revert this!",
          ["Cancel", "Yes, delete it!"]
        )
        .then((willDelete) => {
          if (willDelete) {
            confirmSoftDelete(id).then(() => {
              Util.getInstance().publishEvent("fetchData");
              fetchList();
            });
          }
        });
    };

    const onTableChange = (newPagination, filters, sorter) => {
      fetchList({
        sortField: sorter.field,
        sortOrder: sorter.order,
        pagination: newPagination,
        ...filters,
      });
    };

    const onChangeTopFilter = (key) => {
      const queryParams = new URLSearchParams(window.location.search);
      if (key && key !== "null") {
        queryParams.set("current", 1);
        queryParams.set("status", key);
      } else {
        queryParams.delete("status");
      }
      Util.getInstance().pushParamsToURL(pathName, queryParams.toString());
      fetchList();
    };

    const onChangePagination = (page, pageSize) => {
      const params = new URLSearchParams(document.location.search);
      params.set("current", page);
      params.set("size", pageSize);
      Util.getInstance().pushParamsToURL(pathName, params.toString());
      fetchList();
    };

    const onShowSizeChange = (current, size) => {
      const params = new URLSearchParams(document.location.search);
      params.set("current", 1);
      params.set("size", size);
      Util.getInstance().pushParamsToURL(pathName, params.toString());
      fetchList();
    };

    React.useMemo(() => {
        const params = new URLSearchParams(document.location.search);
        if (!params.get("date")) params.set("date", `${moment().format("YYYY-MM")},${moment().format("YYYY-MM")}`);
        Util.getInstance().pushParamsToURL(pathName, params.toString());
    }, []);

    const onBack = () => {
      navigate("/reports/leaves");
    };

    React.useEffect(() => {
      fetchList();
      // eslint-disable-next-line
    }, []);
    const pagination = {
      current: params.has("current") ? parseInt(params.get("current")) : 1,
      defaultPageSize,
      pageSizeOptions: ["20", "30", "50", "100"],
      total: datas.pagination?.total,
      showSizeChanger: true,
      locale: { items_per_page: "" },
      onChange: onChangePagination,
      onShowSizeChange,
      showTotal: (total, range) =>
        `${t("text_display")} ${range[0]}-${range[1]} ${t(
          "text_out_of"
        )} ${total}`,
    };

    const onChangeDate = (values) => {
      const queryParams = new URLSearchParams(document.location.search);
      queryParams.set("current", 1);
      if (values) {
        const dates = `${moment(values[0]).format("YYYY-MM")},${moment(
          values[1]
        ).format("YYYY-MM")}`;
        queryParams.set("date", dates);
      } else {
        queryParams.delete("date");
      }
      Util.getInstance().pushParamsToURL(
        "/reports/leaves/daily-leave-status",
        queryParams.toString()
      );
      fetchList();
    };

    function getDataSelectSearch(value) {
      if (value) {
        const search = JSON.stringify({
          column: ["name"],
          value: value,
        });
        return fetch(
          `${getURl()}/employee/v1/lists?offset=0&limit=15&search=${search}`,
          {
            headers: getHeaders(),
            method: "GET",
          }
        )
          .then((response) => response.json())
          .then((response) =>
            response.data.map((value) => ({
              label: `${value.name}`,
              value: value.id,
            }))
          );
      }
      return [];
    }

    const onChangeSelectSearch = (value) => {
        const queryParams = new URLSearchParams(document.location.search);
        if (value && value.key) {
            queryParams.set("current", 1);
          queryParams.set("employeeId", value.key);
          queryParams.set("employeeName", value.label);
          Util.getInstance().pushParamsToURL(pathName, queryParams.toString());
          fetchList();
        }
        if (value === undefined) {
          queryParams.delete("employeeId");
          queryParams.delete("employeeName");
          Util.getInstance().pushParamsToURL("/reports/leaves/daily-leave-status", queryParams.toString());
          fetchList();
        }
      };

    return (
      <>
        <Row style={styles.breadCream}>
          <Col span={24}>
            <Breadcrumb>
              <Breadcrumb.Item
                onClick={() => navigate("/")}
                style={{ cursor: "pointer" }}
              >
                {t("text_dashboard")}
              </Breadcrumb.Item>
              <Breadcrumb.Item onClick={onBack} style={{ cursor: "pointer" }}>
                Leave Reports
              </Breadcrumb.Item>
              <Breadcrumb.Item style={{ color: "#1d8747" }}>
                Monthly Leave Report
              </Breadcrumb.Item>
              <Breadcrumb.Item style={{ color: "#1d8747" }}></Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>

        <TopFilter items={topFilterItems} onChange={onChangeTopFilter} />

        <Row style={{ padding: 15 }}>
          <Col span={8}>
            <Title title={"Monthly Leave Report"} />
          </Col>
          <Col span={16} style={{ textAlign: "right", bottom: "5px",display: "flex", justifyContent: "flex-end" }}>
            <div id="main-select">
              <SearchSelectInput
                name="employeeId"
                placeholder={t("text_search_customer")}
                fetchOptions={getDataSelectSearch}
                defaultValue={params.get("employeeName")}
                onChange={onChangeSelectSearch}
                style={styles.formInline}
              />
            </div>
            <RangePicker
              name="dates"
              placeholder={["MM/YYYY", "MM/YYYY"]}
              format={["MM/YYYY", "MM/YYYY"]}
              value={params.get("date") ? [moment(params.get("date").split(",")[0]), moment(params.get("date").split(",")[1])] : []}
              onChange={onChangeDate}
              allowClear={false}
              style={{
                width: "330px",
                height: "32px",
              }}
              picker="month"
              separator={"~"}
            />
            {React.useMemo(() => (<ExportButton enable={true} filtExport={buttonOption} />), [])}
          </Col>
          
        </Row>

        <div className="data-table" style={{ flex: 1, display: "block" }}>
          <Table
            dataSource={datas.data}
            pagination={pagination}
            onChange={onTableChange}
            rowKey={(record) => `${record.id}`}
            scroll={
              datas.pagination && datas.pagination.count > 10
                ? {
                    y: tableScrollY
                      ? tableScrollY
                      : window.innerHeight -
                        (310 + (topFilterItems.length > 0 ? 60 : 0)),
                  }
                : {}
            }
            columns={[
              ...columns,
              {
                title: t("text_action"),
                dataIndex: "id",
                key: "id",
                width: 160,
                render: (id) => {
                  return (
                    <div id="btn-action">
                      <div id="btnInfos" className="btn-infos">
                        {showButtonInfo && (
                          <Button
                            id="info-btn"
                            title={"Info"}
                            type="default"
                            className="info"
                            icon={<InfoCircleOutlined />}
                            onClick={() => onShowDetail(id)}
                            style={{
                              height: 35,
                              width: 35,
                              borderRadius: 35,
                            }}
                          />
                        )}
                      </div>

                      <div id="btnDelete" className="btn-delete">
                        {showButtonDelete && (
                          <Button
                            danger
                            title={t("text_delete")}
                            icon={<DeleteFilled style={{ color: "#fff" }} />}
                            shape="circle"
                            type="primary"
                            style={{
                              marginLeft: 5,
                              height: 35,
                              width: 35,
                              borderRadius: 35,
                              border: "none",
                            }}
                            onClick={() => onDelete(id)}
                          />
                        )}
                      </div>
                    </div>
                  );
                },
              },
            ]}
            loading={loading}
          />
        </div>

        <FormInsertOrUpdate
          onSuccess={fetchList}
          form={form}
          navigate={navigate}
        />
        <FormReview
          ref={(f) => (reviewRef = f)}
          onSuccess={fetchList}
          navigate={navigate}
        />
      </>
    );
  };
}

function ExportButton({ enable, filtExport }) {
    const [visible, setVisible] = React.useState(false);
  
    const onClose = () => {
      setVisible(false);
    };
  
    const onShow = () => {
      setVisible(true);
    };
  
    return (
      enable && (
        <>
          <Button
            icon={<ExportOutlined />}
            onClick={onShow}
            style={{ marginLeft: 10 }}
          >
            {t("text_export")}
          </Button>
          <Drawer
            title={
              <div style={styles.headerFilter}>
                <div>{t("text_export_report")}</div>
                <div>
                  <CloseOutlined onClick={onClose} />
                </div>
              </div>
            }
            placement="right"
            closable={false}
            onClose={onClose}
            visible={visible}
          >
            {visible ? <ExportExcelFile filtExport={filtExport} /> : null}
          </Drawer>
        </>
      )
    );
  }

  const ExportExcelFile = ({ filtExport }) => {
    return filtExport();
  }

function Title({ title }) {
  return <h4 style={{ marginBottom: 0, fontWeight: 600 }}>{title}</h4>;
}

function TopFilter({ items, onChange }) {
  const { TabPane } = Tabs;
  const params = new URLSearchParams(document.location.search);
  const defaultKey = params.get("status");
  return (
    items.length > 0 && (
      <Tabs
        defaultActiveKey={defaultKey}
        onChange={onChange}
        className="padding-tabe"
        id="tabs"
      >
        {items.map((item) => (
          <TabPane tab={item.name} key={item.value} />
        ))}
      </Tabs>
    )
  );
}

const styles = {
    formInline: {
        width: 203,
        display: "inline-block",
        textAlign: "left",
        marginRight: 5,
        marginBottom: 0,
        marginTop: 0,
      },
  generalSearch: {
    width: 200,
  },
  headerFilter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  breadCream: {
    paddingBottom: 15,
    backgroundColor: "#EFF3F6",
  },
  style: {
    background: "#0092ff",
    padding: "8px 0",
  },
};
